/**
 * The Cofactor Venn mark, physically simulated: the two discs bounce inside
 * the puck's circular wall while it's dragged or flying to a corner, then
 * settle back into the exact static logo at rest.
 *
 * Geometry is the square `viewBox="0 0 80 80"` variant already shipped as
 * `apps/web/public/favicon-cofactor.svg` (not the 4:3 `CofactorMark` in
 * `ui/components/CofactorMark.tsx`) — a square box gives a true circular containment
 * boundary concentric with the puck. The four moving circles are driven
 * imperatively via `setAttribute` from a `requestAnimationFrame` loop — no
 * per-frame React state, so dragging the puck never re-renders React.
 */

import React from 'react';
import { useReducedMotion } from 'framer-motion';
import {
  applyImpulse,
  applyRadialImpulse,
  createBodies,
  isAtRest,
  stepBodies,
  DISC_RADIUS,
  REST_POSITIONS,
  type Body,
  type PendingImpulse,
} from './discPhysics';
import type { Point } from './cornerPin';

const MAX_DT = 1 / 30;
// Static SVG defaults for the two discs — sourced from the same
// `REST_POSITIONS` the physics sim rests at, so retuning the sim can never
// silently desync the static geometry from the simulated rest state.
const [REST_LEFT, REST_RIGHT] = REST_POSITIONS;

export type LogoMarkPhysicsProps = {
  size: number;
  className?: string;
  /** Latest puck acceleration in SVG units/s^2, read once per animation frame. */
  accelRef: React.RefObject<Point>;
  /** True while the puck is being dragged or is flying to a corner. */
  activeRef: React.RefObject<boolean>;
  /**
   * One-shot impulse (e.g. the corner "landing thunk", or the open/dismiss
   * pop). Consumed and cleared on the next frame the loop runs.
   */
  impulseRef: React.MutableRefObject<PendingImpulse | null>;
  /** Bump to restart the physics loop (e.g. on drag start / landing). */
  runToken: number;
};

export function LogoMarkPhysics({
  size,
  className,
  accelRef,
  activeRef,
  impulseRef,
  runToken,
}: LogoMarkPhysicsProps) {
  const id = React.useId();
  const clipId = `${id}-clip`;
  const maskId = `${id}-mask`;

  const leftRef = React.useRef<SVGCircleElement>(null);
  const rightRef = React.useRef<SVGCircleElement>(null);
  const maskLeftRef = React.useRef<SVGCircleElement>(null);
  const maskRightBaseRef = React.useRef<SVGCircleElement>(null);
  const maskRightKnockoutRef = React.useRef<SVGCircleElement>(null);
  const clipCircleRef = React.useRef<SVGCircleElement>(null);

  const bodiesRef = React.useRef<[Body, Body]>(createBodies());
  const rafRef = React.useRef<number | null>(null);
  const lastTimeRef = React.useRef<number | null>(null);
  const reducedMotion = useReducedMotion();

  const applyBodies = React.useCallback((bodies: [Body, Body]) => {
    const [left, right] = bodies;
    leftRef.current?.setAttribute('cx', String(left.x));
    leftRef.current?.setAttribute('cy', String(left.y));
    rightRef.current?.setAttribute('cx', String(right.x));
    rightRef.current?.setAttribute('cy', String(right.y));
    maskLeftRef.current?.setAttribute('cx', String(left.x));
    maskLeftRef.current?.setAttribute('cy', String(left.y));
    maskRightBaseRef.current?.setAttribute('cx', String(right.x));
    maskRightBaseRef.current?.setAttribute('cy', String(right.y));
    maskRightKnockoutRef.current?.setAttribute('cx', String(right.x));
    maskRightKnockoutRef.current?.setAttribute('cy', String(right.y));
    clipCircleRef.current?.setAttribute('cx', String(left.x));
    clipCircleRef.current?.setAttribute('cy', String(left.y));
  }, []);

  React.useEffect(() => {
    if (reducedMotion) return;

    const tick = (time: number) => {
      const last = lastTimeRef.current;
      lastTimeRef.current = time;
      const dt = last === null ? 0 : Math.min((time - last) / 1000, MAX_DT);

      if (impulseRef.current) {
        const impulse = impulseRef.current;
        if (impulse.kind === 'translational') {
          applyImpulse(bodiesRef.current, impulse.vector);
        } else {
          applyRadialImpulse(bodiesRef.current, impulse.magnitude);
        }
        impulseRef.current = null;
      }

      if (dt > 0) {
        stepBodies(bodiesRef.current, accelRef.current ?? { x: 0, y: 0 }, dt);
        applyBodies(bodiesRef.current);
      }

      if (isAtRest(bodiesRef.current) && !activeRef.current) {
        rafRef.current = null;
        lastTimeRef.current = null;
        return;
      }
      rafRef.current = requestAnimationFrame(tick);
    };

    rafRef.current = requestAnimationFrame(tick);

    return () => {
      if (rafRef.current !== null) cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
      lastTimeRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- restart is driven by runToken
  }, [runToken, reducedMotion, applyBodies, accelRef, activeRef, impulseRef]);

  return (
    <svg
      className={className}
      width={size}
      height={size}
      viewBox="0 0 80 80"
      fill="none"
      aria-hidden="true"
      style={{ display: 'inline-block', flexShrink: 0 }}
    >
      <defs>
        <clipPath id={clipId}>
          <circle ref={clipCircleRef} cx={REST_LEFT.x} cy={REST_LEFT.y} r={DISC_RADIUS} />
        </clipPath>
        <mask id={maskId}>
          <circle ref={maskLeftRef} cx={REST_LEFT.x} cy={REST_LEFT.y} r={DISC_RADIUS} fill="#fff" />
          <circle
            ref={maskRightBaseRef}
            cx={REST_RIGHT.x}
            cy={REST_RIGHT.y}
            r={DISC_RADIUS}
            fill="#fff"
          />
          {/* knock out the intersection so the background shows through */}
          <circle
            ref={maskRightKnockoutRef}
            cx={REST_RIGHT.x}
            cy={REST_RIGHT.y}
            r={DISC_RADIUS}
            fill="#000"
            clipPath={`url(#${clipId})`}
          />
        </mask>
      </defs>
      <g mask={`url(#${maskId})`}>
        <circle ref={leftRef} cx={REST_LEFT.x} cy={REST_LEFT.y} r={DISC_RADIUS} fill="currentColor" />
        <circle
          ref={rightRef}
          cx={REST_RIGHT.x}
          cy={REST_RIGHT.y}
          r={DISC_RADIUS}
          fill="hsl(var(--primary))"
        />
      </g>
    </svg>
  );
}
